import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Badge, Button, Container } from "reactstrap";
import { RefreshCw } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Loader, TablePagination } from "../reporting/ReportingShared";
import AdjustWalletModal from "./AdjustWalletModal";
import { formatCurrency, formatSignedCurrency, formatDateTime } from "../../utils/formatCurrency";
import { getWalletLiability, getWalletCustomers, getWalletLedger, getWalletTopups } from "../../utils/apiCalls";

const EVENT_LABELS = {
  topup: "Top-up",
  topup_bonus: "Top-up bonus",
  topup_reversal: "Chargeback clawback",
  signup_bonus: "Signup bonus",
  admin_credit: "Admin credit",
  admin_debit: "Admin debit",
  order_spend: "Order payment",
  order_spend_reversal: "Order payment reversed",
  order_refund_to_wallet: "Refund to wallet",
  transfer_out: "Transfer sent",
  transfer_in: "Transfer received",
  transfer_reversal: "Transfer reversed",
  expiry: "Expired",
  adjustment: "Adjustment",
};

const TOPUP_STATUS_COLORS = {
  credited: { bg: C.greenLight, color: C.green },
  paid: { bg: C.orangeLight, color: C.orange },
  pending: { bg: C.borderLight, color: C.textMed },
  failed: { bg: "#FEF2F2", color: "#B91C1C" },
  expired: { bg: C.borderLight, color: C.textLight },
  refunded: { bg: C.purpleLight, color: C.purple },
};

const TABS = [
  { key: "customers", label: "Customers" },
  { key: "ledger", label: "Ledger" },
  { key: "topups", label: "Top-ups" },
];

const WalletReport = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;
  const isPlatformAdmin = userType === "admin";

  const [liability, setLiability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("customers");

  const [data, setData] = useState({ rows: [], total: 0, page: 1, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [topupStatus, setTopupStatus] = useState("");

  const [adjustTarget, setAdjustTarget] = useState(null);

  const loadLiability = useCallback(async () => {
    if (!selectedMerchantId) return;
    setLiability(await getWalletLiability(selectedMerchantId));
  }, [selectedMerchantId]);

  const loadTab = useCallback(async () => {
    if (!selectedMerchantId) return;
    setLoading(true);
    const params = { merchantId: selectedMerchantId, page, limit };
    let result;
    if (tab === "customers") result = await getWalletCustomers({ ...params, search: search || undefined });
    else if (tab === "ledger") result = await getWalletLedger({ ...params, eventType: eventType || undefined });
    else result = await getWalletTopups({ ...params, status: topupStatus || undefined });
    setData(result);
    setLoading(false);
  }, [selectedMerchantId, tab, page, limit, search, eventType, topupStatus]);

  useEffect(() => { loadLiability(); }, [loadLiability]);
  useEffect(() => { loadTab(); }, [loadTab]);

  // Reset paging whenever the merchant, the tab or a filter changes — otherwise page 7 of the
  // previous view silently becomes an empty page 7 of the new one.
  useEffect(() => { setPage(1); }, [selectedMerchantId, tab, search, eventType, topupStatus]);

  const exportCsv = () => {
    const rows = data.rows || [];
    if (!rows.length) return;
    const cols = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object" || rows[0][k] === null);
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      // Quote anything containing a delimiter, quote or newline, and double embedded quotes.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => escape(r[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `wallet-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filter control styling, lifted verbatim from devices-list so every reporting page's dropdowns
  // are the same object rather than three near-identical ones.
  const filterSelectStyle = {
    padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`, fontFamily: "inherit",
    outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 130, transition: "border 0.15s",
  };

  if (!selectedMerchantId) {
    return (
      <Fragment>
        <Breadcrumb title="Wallet Report" parent="Customer Wallet" />
        <Container fluid>
          <style>{reportingGlobalCSS}</style>
          <div className="rpt-wrap">
            <RCard>
              <div style={{ padding: 32, textAlign: "center", color: C.textMed, fontSize: 13 }}>
                Please select a merchant to view the customer wallet report.
              </div>
            </RCard>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title="Wallet Report" parent="Customer Wallet" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── KPI Tiles ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
            <Tile
              label="Outstanding liability"
              value={formatCurrency(liability?.purchasedLiability)}
              sub={`purchased credit · ${formatCurrency(liability?.promotionalAccrual, { withCurrency: false })} promotional`}
              icon="💰" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0}
            />
            <Tile
              label="Total in wallets"
              value={formatCurrency(liability?.totalOutstanding)}
              sub={`${liability?.wallets ?? 0} wallets${liability?.frozenWallets ? ` · ${liability.frozenWallets} frozen` : ""}`}
              icon="👛" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80}
            />
            <Tile
              label="Topped up (lifetime)"
              value={formatCurrency(liability?.lifetimeToppedUp)}
              sub={`${formatCurrency(liability?.lifetimeBonus, { withCurrency: false })} given as bonus`}
              icon="📈" gradient={`linear-gradient(135deg, ${C.green}, #7BE8A8)`} delay={160}
            />
            {/* Orange on purpose: this is balance that appeared without a customer paying for it. */}
            <Tile
              label="Admin adjustments"
              value={formatCurrency(liability?.adminCredited)}
              sub={`${formatCurrency(liability?.adminDebited, { withCurrency: false })} deducted`}
              icon="⚠️" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={240}
            />
          </div>

          {liability?.negativeWallets > 0 && (
            <RCard style={{ borderLeft: `4px solid ${C.orange}`, marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: C.text }}>
                <strong>{liability.negativeWallets}</strong> wallet(s) have a negative balance. This
                happens when a top-up was charged back after the credit had already been spent — the
                shortfall is a real loss and those wallets are frozen. Review them under Top-ups.
              </div>
            </RCard>
          )}

          {/* ─── Filter Bar + Table ────────────────────── */}
          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div
                onClick={() => { loadTab(); loadLiability(); }}
                title="Refresh"
                style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", background: C.white, flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.coral; e.currentTarget.style.background = C.coralLight; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                {TABS.map((t) => (
                  <button
                    key={t.key} type="button" onClick={() => setTab(t.key)}
                    style={{
                      padding: "7px 16px", borderRadius: 99, fontSize: 12, fontWeight: 700, cursor: "pointer",
                      border: `1.5px solid ${tab === t.key ? C.coral : C.border}`,
                      background: tab === t.key ? C.coralLight : C.white,
                      color: tab === t.key ? C.coralDark : C.textMed,
                      transition: "all 0.15s",
                    }}
                  >{t.label}</button>
                ))}
              </div>

              {tab === "customers" && (
                <input
                  style={{ ...filterSelectStyle, cursor: "text", minWidth: 220 }}
                  placeholder="Name, phone or email"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                />
              )}
              {tab === "ledger" && (
                <select style={{ ...filterSelectStyle, minWidth: 190 }}
                  value={eventType} onChange={(e) => setEventType(e.target.value)}>
                  <option value="">All events</option>
                  {Object.entries(EVENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              )}
              {tab === "topups" && (
                <select style={{ ...filterSelectStyle, minWidth: 150 }}
                  value={topupStatus} onChange={(e) => setTopupStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  {Object.keys(TOPUP_STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}

              <Button size="sm" color="secondary" outline onClick={exportCsv} disabled={!data.rows?.length}
                style={{ fontSize: 12, fontWeight: 700, borderRadius: 8 }}>
                Export CSV
              </Button>

              <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>
                {data.total ?? 0} {tab === "customers" ? "customers" : tab === "ledger" ? "entries" : "top-ups"}
              </span>
            </div>

            {loading ? <Loader /> : (
              <>
                {tab === "customers" && <CustomersTable rows={data.rows} onAdjust={setAdjustTarget} search={search} />}
                {tab === "ledger" && <LedgerTable rows={data.rows} />}
                {tab === "topups" && <TopupsTable rows={data.rows} />}

                <TablePagination
                  total={data.total} page={page} limit={limit}
                  onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }}
                />
              </>
            )}
          </RCard>
        </div>

        <AdjustWalletModal
          isOpen={!!adjustTarget}
          toggle={() => setAdjustTarget(null)}
          merchantId={selectedMerchantId}
          customer={adjustTarget?.user}
          purchasedBalance={adjustTarget?.purchasedBalance}
          promotionalBalance={adjustTarget?.promotionalBalance}
          isPlatformAdmin={isPlatformAdmin}
          onDone={() => { loadTab(); loadLiability(); }}
        />
      </Container>
    </Fragment>
  );
};

const Empty = ({ children }) => (
  <div style={{ padding: 32, textAlign: "center", color: C.textMed, fontSize: 13 }}>{children}</div>
);

// The reporting table shell used across devices-list and the rest of the reporting pages: the table
// scrolls inside its OWN rounded container, so a wide table never makes the page scroll sideways.
// Defined once here rather than repeated by all three tabs below.
const TableShell = ({ children }) => (
  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
    <table style={tbl.table}>{children}</table>
  </div>
);

// Without a search term the API returns WALLET HOLDERS only — the finance view. Type a name or phone
// and it searches every customer of the brand, wallet or not, so a first credit can be granted to
// someone who has never held balance. Those rows come back with `hasWallet: false`.
const CustomersTable = ({ rows, onAdjust, search }) => {
  if (!rows?.length) {
    return (
      <Empty>
        {search
          ? `No customer of this brand matches “${search}”.`
          : "No wallet balances yet. Search a name or phone number to grant a customer their first credit."}
      </Empty>
    );
  }
  return (
    <TableShell>
      <thead>
        <tr>
          <th style={tbl.th}>Customer</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Purchased</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Promotional</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Total</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Topped up</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Spent</th>
          <th style={tbl.th}>Status</th>
          <th style={tbl.th}>Last activity</th>
          <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          // Keyed on the USER, not the wallet: a searched customer with no wallet yet has
          // walletId === null, and null keys would collide across every such row.
          <tr key={r.user?.id ?? r.walletId} className="rpt-row">
            <td style={tbl.td}>
              <div style={{ fontWeight: 600, color: C.text }}>
                {[r.user?.firstName, r.user?.lastName].filter(Boolean).join(" ") || "—"}
              </div>
              <div style={{ color: C.textLight, fontSize: 12 }}>{r.user?.phoneNo}</div>
            </td>
            <td style={{ ...tbl.td, textAlign: "right" }}>{formatCurrency(r.purchasedBalance)}</td>
            <td style={{ ...tbl.td, textAlign: "right" }}>{formatCurrency(r.promotionalBalance)}</td>
            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, color: r.totalBalance < 0 ? "#B91C1C" : C.text }}>
              {formatCurrency(r.totalBalance)}
            </td>
            <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{formatCurrency(r.lifetimeToppedUp)}</td>
            <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{formatCurrency(r.lifetimeSpent)}</td>
            <td style={tbl.td}>
              {/* `hasWallet === false` is not an "active wallet with nothing in it" — there is no
                  wallet at all yet. Saying so stops a zero reading as a real balance. */}
              {r.hasWallet === false ? (
                <Badge style={{ background: C.borderLight, color: C.textMed }}
                  title="No wallet yet — the first credit will create it">
                  no wallet yet
                </Badge>
              ) : (
                <Badge style={r.status === "active"
                  ? { background: C.greenLight, color: C.green }
                  : { background: "#FEF2F2", color: "#B91C1C" }} title={r.frozenReason || ""}>
                  {r.status}
                </Badge>
              )}
            </td>
            <td style={{ ...tbl.td, color: C.textLight, fontSize: 12 }}>{formatDateTime(r.lastActivityAt)}</td>
            <td style={{ ...tbl.td, textAlign: "center" }}>
              <Button size="sm" color="primary" outline onClick={() => onAdjust(r)}
                style={{ fontSize: 11, fontWeight: 700, borderRadius: 8, padding: "4px 12px" }}>
                Adjust
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
};

const LedgerTable = ({ rows }) => {
  if (!rows?.length) return <Empty>No wallet activity for this filter.</Empty>;
  return (
    <TableShell>
      <thead>
        <tr>
          <th style={tbl.th}>When</th>
          <th style={tbl.th}>Customer</th>
          <th style={tbl.th}>Event</th>
          <th style={tbl.th}>Bucket</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Amount</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Balance after</th>
          <th style={tbl.th}>Order</th>
          <th style={tbl.th}>Reason</th>
          <th style={tbl.th}>By</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="rpt-row">
            <td style={{ ...tbl.td, color: C.textMed, whiteSpace: "nowrap" }}>{formatDateTime(r.createdAt)}</td>
            <td style={tbl.td}>
              <div style={{ fontWeight: 600, color: C.text }}>
                {[r.User?.firstName, r.User?.lastName].filter(Boolean).join(" ") || "—"}
              </div>
              <div style={{ color: C.textLight, fontSize: 11 }}>{r.User?.phoneNo}</div>
            </td>
            <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>{EVENT_LABELS[r.eventType] || r.eventType}</td>
            <td style={{ ...tbl.td, color: C.textMed }}>{r.bucket}</td>
            {/* Signed, using a real minus sign — mistaking a debit for a credit is the specific
                misreading this table must not cause. */}
            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, color: r.amountDelta < 0 ? "#B91C1C" : C.green }}>
              {formatSignedCurrency(r.amountDelta)}
            </td>
            <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{formatCurrency(r.totalBalanceAfter)}</td>
            <td style={tbl.td}>
              {r.OrderId
                ? <span style={{ color: C.blue, fontWeight: 600 }}>#{r.OrderId}</span>
                : <span style={{ color: C.textLight }}>—</span>}
            </td>
            <td style={{ ...tbl.td, color: C.textMed, maxWidth: 220 }}>{r.reason || "—"}</td>
            <td style={{ ...tbl.td, color: C.textLight, fontSize: 12 }}>
              {r.performedByType}{r.performedByUserId ? ` #${r.performedByUserId}` : ""}
            </td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
};

const TopupsTable = ({ rows }) => {
  if (!rows?.length) return <Empty>No top-ups for this filter.</Empty>;
  return (
    <TableShell>
      <thead>
        <tr>
          <th style={tbl.th}>When</th>
          <th style={tbl.th}>Customer</th>
          <th style={tbl.th}>Source</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Paid</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Credited</th>
          <th style={{ ...tbl.th, textAlign: "right" }}>Bonus</th>
          <th style={tbl.th}>Status</th>
          <th style={tbl.th}>Verified by</th>
          <th style={tbl.th}>Reference</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const s = TOPUP_STATUS_COLORS[r.status] || { bg: C.borderLight, color: C.textMed };
          return (
            <tr key={r.id} className="rpt-row">
              <td style={{ ...tbl.td, color: C.textMed, whiteSpace: "nowrap" }}>{formatDateTime(r.createdAt)}</td>
              <td style={tbl.td}>
                <div style={{ fontWeight: 600, color: C.text }}>
                  {[r.User?.firstName, r.User?.lastName].filter(Boolean).join(" ") || "—"}
                </div>
                <div style={{ color: C.textLight, fontSize: 11 }}>{r.User?.phoneNo}</div>
              </td>
              <td style={{ ...tbl.td, color: r.packageName === "Custom" ? C.textMed : C.text }}>
                {r.packageName || "—"}
              </td>
              <td style={{ ...tbl.td, textAlign: "right" }}>{formatCurrency(r.payAmount)}</td>
              <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700 }}>{formatCurrency(r.creditAmount)}</td>
              <td style={{ ...tbl.td, textAlign: "right", color: C.green }}>
                {r.bonusAmount > 0 ? formatCurrency(r.bonusAmount) : "—"}
              </td>
              <td style={tbl.td}>
                <Badge style={{ background: s.bg, color: s.color }} title={r.failureReason || ""}>{r.status}</Badge>
              </td>
              <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{r.verifiedSource || "—"}</td>
              <td style={{ ...tbl.td, color: C.textLight, fontSize: 11, fontFamily: "monospace" }}>{r.externalId}</td>
            </tr>
          );
        })}
      </tbody>
    </TableShell>
  );
};

export default WalletReport;
